/**
 * Per-organization Meta Pixel (browser).
 *
 * Security: only the public Pixel ID is used — never a Conversions API access token.
 * Optimization: script loads once, events no-op when unset, Purchase/Schedule deduped per appointment.
 */

export const META_CURRENCY = "KWD";

export type MetaContentItem = {
  id: string;
  quantity: number;
  item_price?: number;
};

export type MetaCommerceParams = {
  content_ids?: string[];
  contents?: MetaContentItem[];
  content_type?: "product" | "product_group";
  content_name?: string;
  content_category?: string;
  value?: number;
  currency?: string;
  num_items?: number;
};

export type MetaAdvancedMatching = {
  em?: string | null;
  ph?: string | null;
  fn?: string | null;
  ln?: string | null;
  external_id?: string | number | null;
};

type FbqFunction = {
  (...args: unknown[]): void;
  callMethod?: (...args: unknown[]) => void;
  queue?: unknown[];
  loaded?: boolean;
  version?: string;
  push?: (...args: unknown[]) => void;
};

declare global {
  interface Window {
    fbq?: FbqFunction;
    _fbq?: FbqFunction;
  }
}

const SCRIPT_ID = "meta-pixel-fbevents";
const PURCHASE_STORAGE_PREFIX = "meta_pixel_purchase:";

let activePixelId: string | null = null;
let scriptLoadPromise: Promise<void> | null = null;

export const isValidMetaPixelId = (pixelId: string | null | undefined): pixelId is string =>
  typeof pixelId === "string" && /^\d{10,20}$/.test(pixelId.trim());

const normalizeEmail = (email?: string | null) => {
  const value = email?.trim().toLowerCase();
  return value || undefined;
};

const normalizePhone = (phone?: string | null) => {
  if (!phone) return undefined;
  const digits = phone.replace(/\D/g, "");
  return digits || undefined;
};

const normalizeName = (name?: string | null) => {
  const value = name?.trim().toLowerCase();
  return value || undefined;
};

const buildAdvancedMatchingPayload = (user?: MetaAdvancedMatching | null) => {
  if (!user) return undefined;

  const payload: Record<string, string> = {};
  const em = normalizeEmail(user.em);
  const ph = normalizePhone(user.ph);
  const fn = normalizeName(user.fn);
  const ln = normalizeName(user.ln);
  const externalId =
    user.external_id !== null && user.external_id !== undefined
      ? String(user.external_id)
      : undefined;

  if (em) payload.em = em;
  if (ph) payload.ph = ph;
  if (fn) payload.fn = fn;
  if (ln) payload.ln = ln;
  if (externalId) payload.external_id = externalId;

  return Object.keys(payload).length ? payload : undefined;
};

const ensureFbqStub = () => {
  if (typeof window === "undefined") return;

  if (window.fbq) return;

  const fbq: FbqFunction = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args);
    } else {
      (fbq.queue = fbq.queue || []).push(args);
    }
  };
  fbq.push = fbq;
  fbq.loaded = true;
  fbq.version = "2.0";
  fbq.queue = [];
  window.fbq = fbq;
  if (!window._fbq) window._fbq = fbq;
};

const loadPixelScript = (): Promise<void> => {
  if (typeof window === "undefined" || typeof document === "undefined") {
    return Promise.resolve();
  }

  if (document.getElementById(SCRIPT_ID)) {
    return Promise.resolve();
  }

  if (scriptLoadPromise) return scriptLoadPromise;

  ensureFbqStub();

  scriptLoadPromise = new Promise((resolve) => {
    const script = document.createElement("script");
    script.id = SCRIPT_ID;
    script.async = true;
    script.src = "https://connect.facebook.net/en_US/fbevents.js";
    script.onload = () => resolve();
    script.onerror = () => resolve();
    const firstScript = document.getElementsByTagName("script")[0];
    firstScript?.parentNode?.insertBefore(script, firstScript);
  });

  return scriptLoadPromise;
};

const track = (eventName: string, params?: MetaCommerceParams, eventId?: string) => {
  if (!activePixelId || typeof window === "undefined" || !window.fbq) return;

  const options = eventId ? { eventID: eventId } : undefined;
  if (options) {
    window.fbq("track", eventName, params || {}, options);
  } else {
    window.fbq("track", eventName, params || {});
  }
};

const trackCustom = (eventName: string, params?: Record<string, unknown>, eventId?: string) => {
  if (!activePixelId || typeof window === "undefined" || !window.fbq) return;

  const options = eventId ? { eventID: eventId } : undefined;
  if (options) {
    window.fbq("trackCustom", eventName, params || {}, options);
  } else {
    window.fbq("trackCustom", eventName, params || {});
  }
};

/**
 * Initialize (or no-op) the org's Meta Pixel. Safe to call repeatedly.
 * Pass advancedMatching after login for better attribution (Meta hashes client-side).
 */
export const initMetaPixel = async (
  pixelId: string | null | undefined,
  advancedMatching?: MetaAdvancedMatching | null
) => {
  if (!isValidMetaPixelId(pixelId) || typeof window === "undefined") {
    return;
  }

  const normalizedId = pixelId.trim();
  const matching = buildAdvancedMatchingPayload(advancedMatching);

  await loadPixelScript();
  ensureFbqStub();

  if (activePixelId === normalizedId) {
    // Re-init with matching data if user just logged in
    if (matching && window.fbq) {
      window.fbq("init", normalizedId, matching);
    }
    return;
  }

  // One SPA tenant per page load — avoid double-init with a different pixel
  if (activePixelId && activePixelId !== normalizedId) {
    return;
  }

  if (matching) {
    window.fbq?.("init", normalizedId, matching);
  } else {
    window.fbq?.("init", normalizedId);
  }

  activePixelId = normalizedId;
  track("PageView");
};

export const trackPageView = () => track("PageView");

export const trackViewContent = (params: MetaCommerceParams) =>
  track("ViewContent", {
    content_type: "product",
    currency: META_CURRENCY,
    ...params,
  });

export const trackAddToCart = (params: MetaCommerceParams) =>
  track("AddToCart", {
    content_type: "product",
    currency: META_CURRENCY,
    ...params,
  });

export const trackInitiateCheckout = (params: MetaCommerceParams) =>
  track("InitiateCheckout", {
    content_type: "product",
    currency: META_CURRENCY,
    ...params,
  });

export const trackAddPaymentInfo = (params?: MetaCommerceParams & { payment_method?: string }) =>
  track("AddPaymentInfo", {
    content_type: "product",
    currency: META_CURRENCY,
    ...params,
  });

/**
 * Appointment booked. Fires Schedule (Meta standard for bookings) and Purchase (for paid ROAS).
 * Deduped per appointment id so confirmation + refresh don't double-count.
 */
export const trackAppointmentBooked = (args: {
  appointmentId: string | number;
  value: number;
  contents?: MetaContentItem[];
  contentIds?: string[];
  numItems?: number;
  eventId?: string;
}) => {
  const appointmentKey = String(args.appointmentId);
  const storageKey = `${PURCHASE_STORAGE_PREFIX}${appointmentKey}`;

  try {
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(storageKey)) {
      return;
    }
    sessionStorage?.setItem(storageKey, "1");
  } catch {
    // private mode / blocked storage — still fire once this page visit
  }

  const eventId = args.eventId || `appointment_${appointmentKey}`;
  const params: MetaCommerceParams = {
    content_type: "product",
    currency: META_CURRENCY,
    value: Number.isFinite(args.value) ? Math.max(0, args.value) : 0,
    content_ids: args.contentIds,
    contents: args.contents,
    num_items: args.numItems,
  };

  track("Schedule", params, eventId);
  track("Purchase", params, `${eventId}_purchase`);
};

export const trackCompleteRegistration = (params?: Record<string, unknown>) =>
  track("CompleteRegistration", params as MetaCommerceParams);

export const trackLogin = (params?: Record<string, unknown>) => trackCustom("Login", params);

export const trackLead = (params?: Record<string, unknown>) =>
  track("Lead", params as MetaCommerceParams);

export const buildCartMetaParams = (
  articles: Array<{
    totalPrice?: number;
    serviceVariant?: number | null;
    packageVariant?: number | null;
    slots?: unknown[];
  }>
): MetaCommerceParams => {
  const contents: MetaContentItem[] = articles.map((article) => {
    const id = String(article.serviceVariant ?? article.packageVariant ?? "unknown");
    return {
      id,
      quantity: Math.max(1, article.slots?.length || 1),
      item_price: article.totalPrice,
    };
  });

  const value = articles.reduce((sum, article) => sum + (Number(article.totalPrice) || 0), 0);

  return {
    content_ids: contents.map((item) => item.id),
    contents,
    content_type: "product",
    currency: META_CURRENCY,
    value,
    num_items: contents.reduce((sum, item) => sum + item.quantity, 0),
  };
};

export const isMetaPixelActive = () => Boolean(activePixelId);
